import React, {
  ReactNode,
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from 'react';
import { ModuleRegistry, ModuleManifest } from './useModule.ts';
import { resolveApiBaseUrl } from './apiFetch.ts';
import { refreshAccessToken, writeRefreshToken } from './tokenRefresh.ts';
import { revokeSessionOnServer } from './logoutRevocation.ts';

/**
 * Authentication context containing JWT token and user operations
 */
export interface AuthContextType {
  token: string | null;
  setToken: (token: string | null) => void;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  refreshToken: () => Promise<void>;
}

/**
 * Tenant context for multi-tenant support.
 *
 * There is deliberately NO `switchTenant` here. The removed implementation
 * fetched an unauthenticated `/tenants/{id}` route that the gateway does not
 * expose (the real, authenticated contract is `GET /api/v1/auth/tenants`,
 * consumed by {@link useTenant}), and it had zero call sites — a dead code
 * path that would have shipped an unauthenticated tenant read the first time
 * anyone reused it. Workspace switching goes through `setTenant`, which the
 * shell already treats as a full tenant-scope reset: the manifest reloads,
 * the React Query cache is cleared, per-remote federation health is reset,
 * and the realtime bridge re-subscribes for the new tenant. Do not
 * re-introduce a client-side tenant fetch here without an authenticated
 * gateway route to back it.
 */
export interface TenantContextType {
  tenantId: string | null;
  tenantName?: string;
  setTenant: (tenantId: string, name?: string) => void;
}

/**
 * Module context for cross-module communication
 */
export interface ModuleContextType {
  registry: ModuleRegistry | null;
  loadModules: (manifest: ModuleManifest[]) => void;
  currentModule?: string;
  setCurrentModule: (moduleId: string) => void;
}

// Create contexts
export const AuthContext = createContext<AuthContextType | undefined>(
  undefined
);
export const TenantContext = createContext<TenantContextType | undefined>(
  undefined
);
export const ModuleContext = createContext<ModuleContextType | undefined>(
  undefined
);

/**
 * Hook to access auth context
 * Throws error if not within AuthProvider
 */
export const useAuthContext = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within AuthProvider');
  }
  return context;
};

/**
 * Hook to access tenant context
 * Throws error if not within TenantProvider
 */
export const useTenantContext = (): TenantContextType => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenantContext must be used within TenantProvider');
  }
  return context;
};

/**
 * Hook to access module context
 * Throws error if not within ModuleProvider
 */
export const useModuleContext = (): ModuleContextType => {
  const context = useContext(ModuleContext);
  if (!context) {
    throw new Error('useModuleContext must be used within ModuleProvider');
  }
  return context;
};

/**
 * Provider component for authentication
 * Manages JWT token, user login/logout, and token refresh
 */
export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [token, setTokenState] = useState<string | null>(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('auth_token');
    }
    return null;
  });

  const setToken = useCallback((newToken: string | null) => {
    setTokenState(newToken);
    if (typeof window !== 'undefined') {
      if (newToken) {
        localStorage.setItem('auth_token', newToken);
      } else {
        localStorage.removeItem('auth_token');
      }
    }
  }, []);

  const login = useCallback(
    async (email: string, password: string): Promise<void> => {
      const baseUrl = resolveApiBaseUrl();

      const response = await fetch(`${baseUrl}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        throw new Error('Login failed');
      }

      const data = await response.json();
      // Persist the refresh token so silent on-401 refresh can later POST it to
      // the gateway's /auth/refresh (which 400s without one). The access token
      // is short-lived; the refresh token is the only way back to a valid
      // session without re-prompting for credentials.
      writeRefreshToken(data.refreshToken ?? null);
      setToken(data.token);
    },
    [setToken]
  );

  const logout = useCallback(() => {
    // Revoke the server-side refresh-token family FIRST: without this POST the
    // family stays alive on the server and a stolen refresh token keeps
    // working after "logout". revokeSessionOnServer captures the persisted
    // refresh token synchronously (before its first await), so the local
    // clears below cannot starve the in-flight POST — and it never rejects,
    // so a failed revocation can never trap the user logged in (the failure
    // is surfaced via console.warn + the auth:logout-revocation-failed event).
    void revokeSessionOnServer();
    // Clear the refresh token alongside the access token so a logged-out
    // session can't silently refresh itself back in. Unconditional: local
    // logout proceeds whatever the network outcome above.
    writeRefreshToken(null);
    setToken(null);
  }, [setToken]);

  // Delegate to the shared single-flight refresh coordinator so a manual
  // refresh and the automatic on-401 retry share one in-flight request and one
  // strategy. On failure we force logout; on success the coordinator writes the
  // new token to localStorage and emits `auth:token-refreshed`, which the
  // listener below folds into React state.
  const refreshToken = useCallback(async (): Promise<void> => {
    if (!token) {
      throw new Error('No token to refresh');
    }
    const newToken = await refreshAccessToken();
    if (!newToken) {
      logout();
      throw new Error('Token refresh failed');
    }
    setToken(newToken);
  }, [token, logout, setToken]);

  // Fold silent-refresh results (dispatched by the refresh coordinator from
  // inside the API layer's on-401 retry) into React state, and force logout on
  // genuine unauthorized.
  useEffect(() => {
    const handleUnauthorized = () => {
      logout();
    };
    const handleRefreshed = (e: Event) => {
      const detail = (e as CustomEvent<{ token?: string }>).detail;
      if (detail?.token) setToken(detail.token);
    };

    window.addEventListener('auth:unauthorized', handleUnauthorized);
    window.addEventListener('auth:token-refreshed', handleRefreshed);
    return () => {
      window.removeEventListener('auth:unauthorized', handleUnauthorized);
      window.removeEventListener('auth:token-refreshed', handleRefreshed);
    };
  }, [logout, setToken]);

  const value: AuthContextType = {
    token,
    setToken,
    isAuthenticated: !!token,
    login,
    logout,
    refreshToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Provider component for tenant management
 * Handles multi-tenant switching and context
 */
export const TenantProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [tenantId, setTenantIdState] = useState<string | null>(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('tenant_id');
    }
    return null;
  });

  const [tenantName, setTenantName] = useState<string | undefined>(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('tenant_name') || undefined;
    }
    return undefined;
  });

  const setTenant = useCallback((id: string, name?: string) => {
    setTenantIdState(id);
    setTenantName(name);

    if (typeof window !== 'undefined') {
      localStorage.setItem('tenant_id', id);
      if (name) {
        localStorage.setItem('tenant_name', name);
      }
    }
  }, []);

  const value: TenantContextType = {
    tenantId,
    tenantName,
    setTenant,
  };

  return (
    <TenantContext.Provider value={value}>{children}</TenantContext.Provider>
  );
};

/**
 * Provider component for module registry
 * Manages loaded UI modules and cross-module navigation
 */
export const ModuleProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [registry, setRegistry] = useState<ModuleRegistry | null>(null);
  const [currentModule, setCurrentModule] = useState<string>('');

  const loadModules = useCallback((manifests: ModuleManifest[]) => {
    const modules: Record<string, ModuleManifest> = {};
    manifests.forEach((manifest) => {
      modules[manifest.id] = manifest;
    });

    setRegistry({
      modules,
      getModule: (moduleId: string) => modules[moduleId],
      getView: (moduleId: string, viewId: string) => {
        const module = modules[moduleId];
        if (!module?.views) return undefined;
        return module.views.find((v) => v.id === viewId);
      },
    });
  }, []);

  const value: ModuleContextType = {
    registry,
    loadModules,
    currentModule,
    setCurrentModule,
  };

  return (
    <ModuleContext.Provider value={value}>{children}</ModuleContext.Provider>
  );
};

/**
 * Combined provider component for all contexts
 * Wraps AuthProvider, TenantProvider, and ModuleProvider
 */
export const TyndoProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  return (
    <AuthProvider>
      <TenantProvider>
        <ModuleProvider>{children}</ModuleProvider>
      </TenantProvider>
    </AuthProvider>
  );
};
